import { useState } from "react";
import { useSearch } from "wouter";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Shield, Upload, CheckCircle2, AlertCircle, Loader2, FileImage, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const VITE_APP_LOGO = import.meta.env.VITE_APP_LOGO;

function UploadField({ label, hint, onFile }: { label: string; hint: string; onFile: (f: File) => void }) {
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    setFileName(file.name);
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
    onFile(file);
  };
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-gray-700">{label}</Label>
      <p className="text-xs text-gray-500">{hint}</p>
      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#84ab4a] hover:bg-[#84ab4a]/5 transition-colors">
        {preview ? (
          <img src={preview} alt="preview" className="h-full w-full object-contain rounded-xl p-1" />
        ) : fileName ? (
          <div className="flex flex-col items-center gap-1 text-center px-4">
            <FileImage className="w-6 h-6 text-[#84ab4a]" />
            <span className="text-xs text-gray-600 truncate max-w-full">{fileName}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1 text-center px-4">
            <Upload className="w-6 h-6 text-gray-400" />
            <span className="text-xs text-gray-500">Click to upload</span>
          </div>
        )}
        <input type="file" className="hidden" accept="image/*,.pdf" onChange={handleChange} />
      </label>
    </div>
  );
}

export default function AgentVerifyPortal() {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const token = params.get("token") ?? "";

  const sessionQuery = trpc.agentVerify.getSession.useQuery(
    { token },
    { enabled: !!token, retry: false }
  );

  const submitVerification = trpc.identity.submitVerification.useMutation({
    onSuccess: () => {
      toast.success("Documents submitted! The admin will review and notify you.");
      setSubmitted(true);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const [form, setForm] = useState({ legalFullName: "", residentialAddress: "" });
  const [frontFile, setFrontFile] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.legalFullName.trim()) { toast.error("Legal full name is required"); return; }
    if (!form.residentialAddress.trim()) { toast.error("Residential address is required"); return; }
    if (!frontFile) { toast.error("Please upload your ID photo"); return; }
    const rep = sessionQuery.data?.rep;
    if (!rep?.id) { toast.error("Session error — please use the link from your email"); return; }

    setUploading(true);
    try {
      // Upload file to S3 via the identity upload endpoint. Authorized by
      // the SAME verification token used to load this session — the server
      // derives repId from the token, never trusts a client-supplied repId.
      const formData = new FormData();
      formData.append("file", frontFile);
      const uploadRes = await fetch("/api/upload/driver-license?token=" + encodeURIComponent(token), { method: "POST", body: formData });
      if (!uploadRes.ok) throw new Error("File upload failed");
      const { url: driverLicenseFrontUrl, fileKey: driverLicenseFrontKey } = await uploadRes.json();

      submitVerification.mutate({
        repId: rep.id,
        token,
        legalFullName: form.legalFullName,
        residentialAddress: form.residentialAddress,
        driverLicenseFrontUrl,
        driverLicenseFrontKey,
      });
    } catch (err: any) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invalid Link</h1>
          <p className="text-white/60">This verification link is missing a token. Please use the link from your email.</p>
        </div>
      </div>
    );
  }

  if (sessionQuery.isLoading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#84ab4a] animate-spin" />
      </div>
    );
  }

  if (sessionQuery.isError || !sessionQuery.data) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Link Expired or Invalid</h1>
          <p className="text-white/60">This verification link has expired or is no longer valid. Please contact your admin to generate a new link.</p>
        </div>
      </div>
    );
  }

  const { rep } = sessionQuery.data;

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-[#84ab4a]/15 border-2 border-[#84ab4a]/40 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#84ab4a]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Documents Submitted!</h1>
          <p className="text-white/60 mb-4">
            Your identity documents have been received. The BizConnect360 admin team will review them and notify you within 1–2 business days.
          </p>
          <div className="bg-[#0D1E35] border border-[#84ab4a]/20 rounded-xl p-4 text-sm text-white/50">
            Agent: <span className="text-[#84ab4a] font-semibold">{rep?.firstName} {rep?.lastName}</span>
            {rep?.repCode && <span className="ml-2 text-white/30">({rep.repCode})</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="https://bc360.wibiz.ai/wp-content/uploads/2026/03/bc360.png" alt="BizConnect360" className="h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-1">Identity Verification</h1>
          <p className="text-white/60 text-sm">
            Hi <span className="text-[#84ab4a] font-semibold">{rep?.firstName}</span>, please complete your identity verification to activate your agent account.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#0D1E35] border border-white/10 rounded-2xl p-6 shadow-xl">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-white/10">
            <Shield className="w-5 h-5 text-[#84ab4a]" />
            <span className="text-white font-semibold text-sm">Secure Verification Form</span>
            <span className="ml-auto text-xs text-white/40 bg-white/5 px-2 py-0.5 rounded-full">256-bit encrypted</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-300">Legal Full Name</Label>
              <Input
                placeholder="As it appears on your government-issued ID"
                value={form.legalFullName}
                onChange={e => setForm(f => ({ ...f, legalFullName: e.target.value }))}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold text-gray-300">Residential Address</Label>
              <Input
                placeholder="Street, City, State, ZIP"
                value={form.residentialAddress}
                onChange={e => setForm(f => ({ ...f, residentialAddress: e.target.value }))}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
              />
            </div>

            <div className="bg-white/5 rounded-xl p-4 space-y-4">
              <p className="text-xs font-semibold text-white/70 uppercase tracking-wide">Government-Issued Photo ID</p>
              <UploadField
                label="ID Photo (Front)"
                hint="Driver's license, passport, or state ID — front side. Clear, well-lit photo required."
                onFile={setFrontFile}
              />
            </div>

            <div className="bg-[#84ab4a]/10 border border-[#84ab4a]/20 rounded-xl p-3 text-xs text-[#84ab4a]/80">
              <strong>Privacy Notice:</strong> Your documents are encrypted and used solely for identity verification. They will not be shared with third parties.
            </div>

            <Button
              type="submit"
              disabled={uploading || submitVerification.isPending}
              className="w-full bg-[#84ab4a] hover:bg-[#6d9038] text-white font-semibold h-11"
            >
              {(uploading || submitVerification.isPending) ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
              ) : (
                <><Shield className="w-4 h-4 mr-2" /> Submit for Verification</>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-white/30 text-xs mt-4">
          Questions? Contact <a href="mailto:info@BizConnect360.com" className="text-[#84ab4a] hover:underline">info@BizConnect360.com</a>
        </p>
      </div>
    </div>
  );
}
